import { UseFormReturn } from 'react-hook-form';

import { Box } from 'lucide-react';

import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { LoadTextInput } from 'pages/LoadView/LoadInformation/Components';
import { Commodity } from 'types/Load';

import { TransportProFormLoad } from '../types';

type CommodityItemCardProps = {
  index: number;
  formMethods: UseFormReturn<TransportProFormLoad>;
};

function formatDimensionUnit(item: Commodity): string {
  const unit = item.dimensionUnit?.trim();
  if (!unit) {
    return 'in';
  }
  if (unit === 'inches' || unit === 'in') {
    return 'in';
  }
  return unit;
}

export function CommodityItemCard({
  index,
  formMethods,
}: CommodityItemCardProps) {
  const item = formMethods.watch(`commodities.${index}`);
  const basePath = `commodities.${index}` as const;
  const dimensionUnit = formatDimensionUnit(item);

  return (
    <div className='w-full rounded-lg border border-neutral-200 bg-neutral-50 p-3 shadow-sm dark:border-neutral-700 dark:bg-neutral-800/50'>
      <Flex align='center' gap='sm' className='mb-3'>
        <Box className='h-5 w-5 shrink-0 text-neutral-500' />
        <Typography
          variant='body-sm'
          weight='semibold'
          className='text-neutral-900'
        >
          Item {index + 1}
          {item?.description ? `: ${item.description}` : ''}
        </Typography>
      </Flex>

      <Grid cols='1' gap='sm' className='w-full mx-0'>
        <LoadTextInput
          name={`${basePath}.description`}
          label='Description'
          readOnly
        />

        {item?.referenceNumber ? (
          <LoadTextInput
            name={`${basePath}.referenceNumber`}
            label='Reference #'
            readOnly
          />
        ) : null}

        <Grid cols='2' gap='sm' className='w-full mx-0'>
          <LoadTextInput
            name={`${basePath}.weightTotal`}
            label='Net Wt'
            readOnly
            inputType='number'
          />
          <LoadTextInput
            name={`${basePath}.weightUnit`}
            label='Units'
            readOnly
          />
        </Grid>

        <LoadTextInput
          name={`${basePath}.quantity`}
          label='Piece Count'
          readOnly
          inputType='number'
        />

        <Grid cols='3' gap='sm' className='w-full mx-0'>
          <LoadTextInput
            name={`${basePath}.length`}
            label='Length'
            readOnly
            inputType='number'
            inlineDimensionUnit={dimensionUnit}
          />
          <LoadTextInput
            name={`${basePath}.width`}
            label='Width'
            readOnly
            inputType='number'
            inlineDimensionUnit={dimensionUnit}
          />
          <LoadTextInput
            name={`${basePath}.height`}
            label='Height'
            readOnly
            inputType='number'
            inlineDimensionUnit={dimensionUnit}
          />
        </Grid>

        {item?.freightClass ? (
          <LoadTextInput
            name={`${basePath}.freightClass`}
            label='Freight Class'
            readOnly
          />
        ) : null}

        {item?.nmfc ? (
          <LoadTextInput name={`${basePath}.nmfc`} label='NMFC' readOnly />
        ) : null}
      </Grid>
    </div>
  );
}
